<template>
  <v-container class="estado-pedido-page">
    <div class="mb-2">
      <v-btn text color="primary" class="px-2 back-btn" :ripple="false" @click="goBack">
        <v-icon left>mdi-arrow-left</v-icon>
        Volver
      </v-btn>
    </div>

    <div v-if="pedido">
      <div class="page-top">
        <div class="page-top__text">
          <h1 class="page-title">Estado del pedido</h1>
          <p class="page-subtitle">Pedido #{{ pedido.codigo }}</p>
        </div>
        <v-chip
          small
          label
          class="estado-chip"
          :color="estadoChipColor"
          :text-color="estadoChipTextColor"
        >
          {{ pedido.estadoActual || 'Sin estado' }}
        </v-chip>
      </div>

      <div class="status-banner" :class="`status-banner--${alertType}`">
        <v-icon small class="mr-2" :color="statusIconColor">{{ statusIcon }}</v-icon>
        <div>
          <div class="status-banner__title">{{ textAlert }}</div>
          <div class="status-banner__hint">Esta página se actualiza sola cada unos segundos</div>
        </div>
      </div>

      <div class="panel">
        <h3 class="panel-title">{{ pedido.Cliente || 'Tu pedido' }}</h3>
        <div v-if="pedido.Direccion" class="panel-item">
          <v-icon small color="primary">mdi-map-marker</v-icon>
          <span>{{ pedido.Direccion }}</span>
        </div>
        <div v-if="pedido.FormaEntrega" class="panel-item">
          <v-icon small color="primary">{{ iconoFormaEntrega }}</v-icon>
          <span>{{ pedido.FormaEntrega }}</span>
        </div>
        <div v-if="textoHorarioProgramado" class="panel-item">
          <v-icon small color="primary">mdi-clock-outline</v-icon>
          <span>{{ textoHorarioProgramado }}</span>
        </div>
        <div v-if="pedido.FormaPago" class="panel-item">
          <v-icon small color="primary">{{ iconoFormaPago }}</v-icon>
          <span>{{ pedido.FormaPago }}</span>
        </div>
        <div v-if="textoFormaPago" class="panel-item panel-item--secundario">
          {{ textoFormaPago }}
        </div>
      </div>

      <div
        v-if="pedido.fEntrega_id === 2 && clientData && clientData.Direccion"
        class="panel panel--retiro"
      >
        <div class="panel-item panel-item--start">
          <v-icon small color="warning">mdi-store-marker</v-icon>
          <div>
            <div class="retiro-label">Lugar de retiro</div>
            <div>{{ clientData.Direccion }}</div>
          </div>
        </div>
      </div>

      <div class="panel">
        <h3 class="panel-title">Detalle</h3>
        <div
          v-for="(product, i) in (pedido.productos || [])"
          :key="i"
          class="producto-row"
        >
          <v-avatar size="48" rounded class="producto-avatar">
            <v-img v-if="product.img" :src="product.img"></v-img>
            <v-icon v-else color="grey lighten-1">mdi-food</v-icon>
          </v-avatar>
          <div class="producto-info">
            <div class="producto-nombre">{{ product.ProdNombre }}</div>
            <div class="producto-cant">{{ formatCant(product.Cantidad) }}×</div>
          </div>
        </div>
        <div v-if="!(pedido.productos && pedido.productos.length)" class="panel-empty">
          Sin ítems para mostrar
        </div>
      </div>

      <div class="panel">
        <h3 class="panel-title">Importes</h3>
        <div class="importe-row">
          <span>Subtotal</span>
          <span>${{ formatMoney(pedido.Total) }}</span>
        </div>
        <template v-if="pedido.PedidosYaDescuento > 0">
          <div
            v-for="(detalle, index) in detalleDescuentos"
            :key="`descuento_${index}`"
            class="descuento-row"
          >
            <span>
              <strong>{{ detalle.tipo }}:</strong> {{ detalle.descripcion }}
            </span>
            <span>-${{ Number(detalle.monto || 0).toFixed(2) }}</span>
          </div>
          <div class="importe-row importe-row--descuento">
            <span>Total descuentos</span>
            <span>-${{ formatMoney(pedido.PedidosYaDescuento) }}</span>
          </div>
        </template>
        <div v-if="pedido.CostoEnvio > 0" class="importe-row">
          <span>Costo de envío</span>
          <span>${{ formatMoney(pedido.CostoEnvio) }}</span>
        </div>
        <div class="importe-row importe-row--total">
          <span>Total</span>
          <span>${{ formatMoney(total) }}</span>
        </div>
        <div v-if="pedido.ConCuantoPaga > 0" class="importe-row">
          <span>Paga con</span>
          <span>${{ formatMoney(pedido.ConCuantoPaga) }}</span>
        </div>
      </div>

      <v-btn
        v-if="clientData && clientData.Telefono && clientData.Telefono !== ''"
        color="success"
        dark
        block
        rounded
        large
        class="mt-2 whatsapp-btn"
        @click="consultarWhatsapp"
      >
        <v-icon left>mdi-whatsapp</v-icon>
        Consultar por WhatsApp
      </v-btn>
    </div>

    <div v-else class="loading-wrap">
      <v-progress-linear indeterminate color="primary" rounded></v-progress-linear>
    </div>
  </v-container>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import IconosEnum from '@/helpers/iconos.js'

export default {
  name: 'EstadoPedido',
  computed: {
    detalleDescuentos () {
      if (this.pedido && this.pedido.DetalleDescuentos) {
        try {
          return JSON.parse(this.pedido.DetalleDescuentos)
        } catch (e) {
          return []
        }
      }
      return []
    },
    iconoFormaEntrega () {
      if (this.pedido && this.pedido.fEntrega_id === 1) return IconosEnum.Delivery
      return IconosEnum.RetiraSucursal
    },
    total () {
      if (!this.pedido) return 0
      const sub = Number(this.pedido.Total) || 0
      const desc = Number(this.pedido.PedidosYaDescuento) || 0
      const envio = Number(this.pedido.CostoEnvio) || 0
      return sub - desc + envio
    },
    textoFormaPago () {
      if (!this.pedido || !this.clientData || !this.clientData.formasPago) return null
      const formaPago = this.clientData.formasPago.find(e => e.id === this.pedido.fPago_id)
      return formaPago && formaPago.Texto ? formaPago.Texto : null
    },
    iconoFormaPago () {
      if (this.pedido && this.pedido.fPago_id === 2) return IconosEnum.PagoTarjeta
      return IconosEnum.PagoEfectivo
    },
    textoHorarioProgramado () {
      if (!this.pedido) return null
      const retira = this.pedido.TiempoRetiraPYA || this.pedido.tiempoRetiraPYA || ''
      const entrega = this.pedido.TiempoEntregaPYA || this.pedido.tiempoEntregaPYA || ''
      if (retira && String(retira).trim()) {
        return `Retiro programado: ${this.formatHorarioPedido(retira)}`
      }
      if (entrega && String(entrega).trim()) {
        // TiempoEntregaPYA is the scheduled datetime from the pedido column;
        // ignore empty and avoid confusing with ETA config string in TiempoEntrega.
        return `Entrega programada: ${this.formatHorarioPedido(entrega)}`
      }
      return null
    },
    estadoMeta () {
      if (!this.pedido || !this.pedido.estadoActual) {
        return { type: 'info', text: 'Estado del pedido no disponible' }
      }

      const nro = this.pedido.codigo ? `#${this.pedido.codigo}` : ''
      const prefijo = nro ? `Pedido ${nro}: ` : ''
      const estado = String(this.pedido.estadoActual).toLowerCase()

      switch (estado) {
        case 'encargado':
          return { type: 'info', text: `${prefijo}recibido` }
        case 'confirmado':
          return { type: 'success', text: `${prefijo}confirmado` }
        case 'en preparacion':
          return { type: 'info', text: `${prefijo}en preparación` }
        case 'en camino':
          return { type: 'info', text: `${prefijo}en camino` }
        case 'entregado':
          return { type: 'success', text: `${prefijo}entregado` }
        case 'rechazado':
          return { type: 'error', text: `${prefijo}el local rechazó tu pedido` }
        case 'anulado por falta de confirmacion':
          return { type: 'error', text: `${prefijo}anulado por falta de confirmación` }
        case 'pendiente de pago':
          return { type: 'warning', text: `${prefijo}pendiente de pago` }
        case 'error al realizar el pago':
          return { type: 'error', text: `${prefijo}error al realizar el pago` }
        case 'listo para retirar':
          return { type: 'success', text: `${prefijo}listo para retirar` }
        default:
          return { type: 'info', text: `${prefijo}${this.pedido.estadoActual}` }
      }
    },
    alertType () {
      return this.estadoMeta.type
    },
    textAlert () {
      return this.estadoMeta.text
    },
    estadoChipColor () {
      const t = this.alertType
      if (t === 'success') return 'success'
      if (t === 'error') return 'error'
      if (t === 'warning') return 'warning'
      return 'primary'
    },
    estadoChipTextColor () {
      return 'white'
    },
    statusIcon () {
      const t = this.alertType
      if (t === 'success') return 'mdi-check-circle'
      if (t === 'error') return 'mdi-alert-circle'
      if (t === 'warning') return 'mdi-alert'
      return 'mdi-information'
    },
    statusIconColor () {
      const t = this.alertType
      if (t === 'success') return 'success'
      if (t === 'error') return 'error'
      if (t === 'warning') return 'warning'
      return 'primary'
    },
    ...mapGetters({
      clientData: 'App/data'
    }),
    ...mapGetters('Cuenta', ['isLoggedIn'])
  },
  methods: {
    ...mapActions({
      setData: 'App/setDataClient',
      getPedido: 'App/getPedido'
    }),
    ...mapActions('Cuenta', ['fetchMiPedido']),
    goBack () {
      const client = this.$route.params.client
      if (this.isLoggedIn && client) {
        this.$router.push({ name: 'MisPedidos', params: { client } }).catch(() => {})
        return
      }
      if (client) {
        this.$router.push({ name: 'Home', params: { client } }).catch(() => {})
        return
      }
      this.$router.push({ name: 'Tiendas' }).catch(() => {})
    },
    formatMoney (n) {
      return Number(n || 0).toLocaleString('es-AR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },
    formatCant (n) {
      const num = Number(n)
      if (!Number.isFinite(num)) return n
      return Number.isInteger(num) ? String(num) : num.toLocaleString('es-AR', { maximumFractionDigits: 2 })
    },
    formatHorarioPedido (raw) {
      const s = String(raw || '').trim()
      if (!s) return ''
      // API SavePedido: "dd/MM/yyyy hh:mm" (12h) — mostrar legible
      const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/)
      if (m) {
        const day = m[1].padStart(2, '0')
        const month = m[2].padStart(2, '0')
        const year = m[3]
        let hour = parseInt(m[4], 10)
        const min = m[5]
        // Si parece 12h sin AM/PM y viene de ToString("hh:mm"), dejamos como está HH:mm
        const hh = String(hour).padStart(2, '0')
        return `${day}/${month}/${year} ${hh}:${min}`
      }
      return s
    },
    rememberPedidoLocal (id) {
      try {
        const n = parseInt(id, 10)
        if (!Number.isFinite(n)) return
        let list = []
        const raw = localStorage.getItem('mis-pedidos')
        if (raw) {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) list = parsed.map(x => parseInt(x, 10)).filter(Number.isFinite)
        }
        if (!list.includes(n)) list.unshift(n)
        localStorage.setItem('mis-pedidos', JSON.stringify(list.slice(0, 50)))
      } catch (e) {}
    },
    consultarWhatsapp () {
      if (this.clientData && this.clientData.Telefono) {
        window.open(`https://api.whatsapp.com/send?phone=+549${this.clientData.Telefono}&text=Hola%21%20Quisiera%20realizar%20una%20consulta`, '_blank')
      }
    },
    async refrescarEstado () {
      try {
        const request = await this.getPedido({
          id: this.$route.params.id,
          clientCode: this.$route.params.client,
          options: { skipErrorRedirect: true }
        })

        if (!request || !request.data) {
          console.warn('Error: respuesta del servidor inválida al refrescar estado')
          return
        }

        if (request.data) {
          this.pedido = request.data
        }

        const nuevaTienda = request.data && request.data.Tienda ? String(request.data.Tienda).trim() : null

        if (nuevaTienda && nuevaTienda !== '') {
          try {
            await this.setData(request.data.Tienda)
            await this.$store.dispatch('App/setAppLogo', request.data.Tienda)
          } catch (setDataError) {
            console.warn('Error al actualizar datos del cliente:', setDataError)
          }
        }
      } catch (e) {
        console.warn('Error al refrescar estado del pedido:', e)
      }
    },
    async cargarPedido () {
      const id = this.$route.params.id
      const client = this.$route.params.client

      const tryGetPedido = async (withClient) => {
        try {
          const request = await this.getPedido({
            id,
            clientCode: withClient ? client : null,
            options: { skipErrorRedirect: true }
          })
          if (request && request.data) {
            this.rememberPedidoLocal(id)
            return request.data
          }
        } catch (e) {
          console.warn('getPedido falló', { withClient: !!withClient, message: e && e.message })
        }
        return null
      }

      // Primero con código de tienda; si falla (p.ej. validación), sin filtro.
      let data = client ? await tryGetPedido(true) : null
      if (!data) data = await tryGetPedido(false)
      if (data) return data

      // Fallback: historial autenticado (Mis pedidos)
      if (this.isLoggedIn && client) {
        const customer = await this.fetchMiPedido({ clientCode: client, id })
        if (customer) {
          this.rememberPedidoLocal(id)
          return {
            codigo: customer.Id,
            Cliente: customer.NombrePedido || customer.TiendaNombre || '',
            Direccion: '',
            estadoActual: customer.Estado || '',
            FormaEntrega: '',
            FormaPago: '',
            fEntrega_id: null,
            fPago_id: null,
            Tienda: customer.TiendaCodigo || client,
            Total: customer.Total || 0,
            PedidosYaDescuento: 0,
            CostoEnvio: customer.CostoEnvio || 0,
            ConCuantoPaga: 0,
            DetalleDescuentos: null,
            productos: (customer.Lineas || []).map(l => ({
              ProdNombre: l.Nombre,
              Cantidad: l.Cantidad,
              img: l.SrcImg
            }))
          }
        }
      }

      return null
    }
  },
  async mounted () {
    try {
      const data = await this.cargarPedido()
      if (!data) {
        this.$router.push({ name: 'ErrorPage' })
        return
      }

      this.pedido = data

      const tienda = data.Tienda ? String(data.Tienda).trim() : (this.$route.params.client || '')
      if (tienda) {
        try {
          await this.setData(tienda)
          await this.$store.dispatch('App/setAppLogo', tienda)
        } catch (setDataError) {
          console.warn('Error al cargar datos del cliente:', setDataError)
          // No bloquear la vista de estado si falla el branding de la tienda
        }
      }

      this._estadoInterval = setInterval(() => {
        this.refrescarEstado()
      }, 10000)
    } catch (e) {
      console.error('Error al cargar el estado del pedido:', e)
      this.$router.push({ name: 'ErrorPage' })
    }
  },
  beforeDestroy () {
    if (this._estadoInterval) {
      clearInterval(this._estadoInterval)
      this._estadoInterval = null
    }
  },
  data () {
    return {
      pedido: null,
      _estadoInterval: null
    }
  }
}
</script>
<style scoped>
.estado-pedido-page {
  max-width: 640px;
  text-align: left;
  padding-bottom: 32px;
}
.back-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}
.page-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.page-title {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}
.page-subtitle {
  margin: 4px 0 0;
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.9rem;
}
.estado-chip {
  font-weight: 700 !important;
  flex-shrink: 0;
  margin-top: 4px;
}
.status-banner {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 14px;
  margin-bottom: 12px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fafafa;
}
.status-banner--success {
  background: #e8f5e9;
  border-color: #c8e6c9;
}
.status-banner--error {
  background: #ffebee;
  border-color: #ffcdd2;
}
.status-banner--warning {
  background: #fff8e1;
  border-color: #ffe082;
}
.status-banner--info {
  background: #f3e5f5;
  border-color: #e1bee7;
}
.status-banner__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.3;
}
.status-banner__hint {
  margin-top: 2px;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.5);
}
.panel {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.panel--retiro {
  background: #fff8e1;
  border-color: #ffe082;
}
.panel-title {
  margin: 0 0 12px;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.87);
}
.panel-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  margin-bottom: 4px;
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.75);
}
.panel-item--start {
  align-items: flex-start;
}
.panel-item--secundario {
  padding-left: 28px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}
.panel-empty {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.45);
}
.retiro-label {
  font-weight: 700;
  margin-bottom: 2px;
}
.producto-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.producto-row:first-of-type {
  border-top: none;
  padding-top: 0;
}
.producto-avatar {
  flex-shrink: 0;
  background: #f5f5f5;
}
.producto-info {
  min-width: 0;
  flex: 1;
}
.producto-nombre {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.3;
}
.producto-cant {
  margin-top: 2px;
  font-size: 0.8rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.55);
}
.importe-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
  margin-bottom: 4px;
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.75);
}
.importe-row span:last-child {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}
.importe-row--descuento,
.importe-row--descuento span:last-child {
  color: #2e7d32;
  font-weight: 500;
}
.importe-row--total {
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px solid #e0e0e0;
  font-size: 1.15rem;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.87);
}
.importe-row--total span:last-child {
  font-size: 1.2rem;
  font-weight: 800;
}
.descuento-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin: 4px 0;
  padding: 6px 8px;
  font-size: 0.8rem;
  color: #2e7d32;
  background-color: #f5f5f5;
  border-left: 3px solid #2e7d32;
  border-radius: 6px;
}
.whatsapp-btn {
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  font-weight: 700 !important;
  border-radius: 12px !important;
  height: 48px !important;
}
.loading-wrap {
  margin-top: 24px;
}
</style>
